import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
  const [config, setConfig] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    api.get("/config").then(r => setConfig(r.data)).catch(() => {});
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const pendingCloverToken = (typeof window !== "undefined")
        ? window.sessionStorage.getItem("pending_clover_token")
        : null;
      const body = { email, password, name };
      if (pendingCloverToken) body.pending_clover_token = pendingCloverToken;
      const { data } = await api.post("/auth/register", body);
      setUser(data);
      // Consumed — clear so it can't double-link.
      if (pendingCloverToken && typeof window !== "undefined") {
        window.sessionStorage.removeItem("pending_clover_token");
        window.sessionStorage.removeItem("clover_merchant_id");
        window.sessionStorage.removeItem("clover_merchant_name");
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  const updateUser = (u) => setUser(u);

  return (
    <AuthContext.Provider value={{ user, config, login, register, logout, refresh, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
